// =================================================================================
// File: src/components/Modals/WorkshopBookingModalVisitor.jsx
// Description: Workshop booking modal for visitors (Stripe Connect ONLY)
// =================================================================================

import React, { useState } from "react";
import { X, Calendar, Users, DollarSign, Clock, MapPin } from "lucide-react";
import {
  formatPrice,
  formatDateRange,
  getDuration,
} from "../../utils/formatters";
import { supabase } from "../../supabaseClient";

export default function WorkshopBookingModalVisitor({ workshop, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    seats: 1,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const seatsAvailable =
    (workshop.capacity || 0) - (workshop.seats_taken || 0);
  const isFull = seatsAvailable <= 0;

  const totalPrice =
    ((workshop.price_cents || 0) * formData.seats) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      alert("Please fill in your name and email.");
      return;
    }

    if (formData.seats > seatsAvailable && seatsAvailable > 0) {
      alert(`Only ${seatsAvailable} seats available.`);
      return;
    }

    if (!workshop.price_cents || workshop.price_cents <= 0) {
      alert("This workshop does not have a price configured yet.");
      return;
    }

    // Validate studio has Stripe connected
    if (!workshop.studio?.stripe_account_id_resolved) {
      alert(
        "This studio has not set up payment processing yet. " +
        "Please contact them directly to arrange payment or check back later."
      );
      setSubmitting(false);
      return;
    }

    setSubmitting(true);

    try {
      // Get Supabase session
      const { data: sessionData } = await supabase.auth.getSession();
      const hasSession = !!sessionData?.session;
      const accessToken = sessionData?.session?.access_token;
      console.log("[Workshop booking] Session", {
        hasSession,
        hasAccessToken: !!accessToken,
        userId: sessionData?.session?.user?.id,
      });

      if (!accessToken) {
        alert("Please log in to book this workshop.");
        // Trigger login flow (use existing Mudo login modal/route)
        const loginEvent = new CustomEvent("mudo:show-login");
        window.dispatchEvent(loginEvent);
        setSubmitting(false);
        return;
      }

      // TEMP: Log that we're sending Authorization header
      console.log("[Workshop booking] Sending Authorization header:", `Bearer ${accessToken.slice(0, 20)}...`);

      // Call backend checkout endpoint (proxied to :3000 in dev)
      const response = await fetch("/api/workshops/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          workshopId: workshop.id,
          seats: formData.seats,
          attendeeName: formData.name,
          attendeeEmail: formData.email,
          attendeePhone: formData.phone || "",
          attendeeMessage: formData.message || "",
        }),
      });

      // Safe parse: tolerate empty/non-JSON responses
      let raw = "";
      let json = null;
      try {
        raw = await response.text();
        if (raw) json = JSON.parse(raw);
      } catch (e) {
        console.warn("[Workshop booking] Non-JSON response", e);
      }

      if (!response.ok) {
        const msg =
          json?.error ||
          json?.message ||
          raw ||
          "Failed to create checkout session";
        throw new Error(msg);
      }

      if (!json || !json.ok || !json.url) {
        throw new Error("No checkout URL returned");
      }

      // Show redirect message and redirect to Stripe
      alert("Redirecting to payment...");
      window.location.assign(json.url);
    } catch (error) {
      console.error("[Workshop booking] Error:", error);
      alert(
        error?.message ||
          "Failed to start secure payment. Please try again or contact support."
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white text-black rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header with Workshop Image */}
        <div className="relative">
          {workshop.hero_image_url ? (
            <div className="h-48 overflow-hidden">
              <img
                src={workshop.hero_image_url}
                alt={workshop.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          ) : (
            <div className="h-48 bg-gradient-to-br from-amber-500 to-orange-500" />
          )}

          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h2 className="text-2xl font-bold mb-1">
              {workshop.title}
            </h2>
            {workshop.studio && (
              <p className="text-sm opacity-90">
                {workshop.studio.name}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 192px)" }}
        >
          <>
              {/* Workshop Details */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-600">
                      Date & Time
                    </div>
                    <div className="font-medium">
                      {formatDateRange(
                        workshop.start_at,
                        workshop.end_at
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-600">
                      Duration
                    </div>
                    <div className="font-medium">
                      {getDuration(
                        workshop.start_at,
                        workshop.end_at
                      )}{" "}
                      hours
                    </div>
                  </div>
                </div>

                {workshop.studio &&
                  (workshop.studio.city ||
                    workshop.studio.country) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-gray-600">
                          Location
                        </div>
                        <div className="font-medium">
                          {[
                            workshop.studio.city,
                            workshop.studio.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      </div>
                    </div>
                  )}

                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-600">
                      Availability
                    </div>
                    <div className="font-medium">
                      {isFull ? (
                        <span className="text-red-600">
                          Fully booked
                        </span>
                      ) : (
                        <span className="text-green-600">
                          {seatsAvailable} spots available
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-600">
                      Price per person
                    </div>
                    <div className="font-bold text-lg text-amber-600">
                      {formatPrice(
                        workshop.price_cents,
                        workshop.currency
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Workshop Description */}
              {workshop.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">
                    About this workshop
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {workshop.description}
                  </p>
                </div>
              )}

              {/* Workshop Type & Skill Level */}
              {(workshop.type || workshop.skill_level) && (
                <div className="mb-6 flex flex-wrap gap-3">
                  {workshop.type && (
                    <div className="bg-amber-100 text-amber-900 px-3 py-1 rounded-full text-sm font-medium">
                      Type: {workshop.type}
                    </div>
                  )}
                  {workshop.skill_level && (
                    <div className="bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-sm font-medium">
                      Level: {workshop.skill_level}
                    </div>
                  )}
                </div>
              )}

              {/* Booking Form */}
              {isFull ? (
                <div className="text-center py-8 text-gray-600">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">
                    This workshop is fully booked
                  </p>
                  <p className="text-sm mt-2">
                    Please check back later or contact the studio for
                    availability.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <h3 className="font-semibold text-lg mb-4">
                    Your Details
                  </h3>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          phone: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                      placeholder="+61 400 000 000"
                    />
                  </div>

                  {/* Number of Seats */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Seats{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.seats}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seats: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                    >
                      {Array.from(
                        { length: Math.min(seatsAvailable, 10) },
                        (_, i) => i + 1
                      ).map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "seat" : "seats"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message (optional)
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          message: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none resize-none"
                      placeholder="Any special requests or questions..."
                    />
                  </div>

                  {/* Total Price */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">
                        Total Price:
                      </span>
                      <span className="text-2xl font-bold text-amber-600">
                        {workshop.currency === "USD"
                          ? "$"
                          : workshop.currency === "AUD"
                          ? "A$"
                          : ""}
                        {totalPrice.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Payment will be processed securely via
                      Stripe on the next screen.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Redirecting to payment...
                      </>
                    ) : (
                      "Proceed to Payment"
                    )}
                  </button>

                  <p className="text-xs text-center text-gray-500 mt-3">
                    Secure payment processed by Stripe. You'll be redirected to complete your booking.
                  </p>
                </form>
              )}
            </>
        </div>
      </div>
    </div>
  );
}
